/**
 * useRehydrateFavourites
 *
 * Favourites persist to localStorage with the entity's name captured at
 * star-time. Company favourites are rendered verbatim in the sidebar (we
 * deliberately do NOT re-run the client casing heuristic on them, to avoid
 * clobbering deliberate mixed-case like "JPMorgan"). That means a company
 * starred before the backend `display_name` rollout stays frozen with its old
 * raw registry name (e.g. "BLACKSTONE CAPITAL PARTNERS LIMITED").
 *
 * This hook heals those stale entries: for each company favourite whose stored
 * name still *looks* un-standardised, it fetches the entity's current
 * `display_name` from the backend and patches the store in place. Once a
 * favourite has been corrected to a proper display name, the heuristic no
 * longer flags it, so it is never re-fetched — the mechanism is self-limiting
 * and costs nothing steady-state. Newly-starred favourites already carry the
 * backend display name, so they are never flagged either.
 *
 * Person favourites get the same treatment: a stored name that still *looks*
 * un-standardised (ALL-CAPS registry form, or "LAST, FIRST" order) is healed
 * from the backend `canonical_name` (which now flows through
 * `person_display_name_service` at write time). The client-side
 * `normalizePersonName` heuristic on render is the fallback for names this
 * pass hasn't caught up with yet, not a substitute for it.
 */
import { useEffect } from 'react';
import { useQueries } from '@tanstack/react-query';
import { fetchCompanyDetail, fetchPersonaDetail } from '@/features/database/api/databaseApi';
import { normalizeCompanyName, normalizePersonName } from '@/utils/displayName';
import { useFavouritesStore, type FavouriteItem } from './favouritesStore';

// Legal / structural suffixes the backend display-name algorithm strips. A
// stored name still ending in one of these was captured before standardisation.
const LEGAL_SUFFIX_RE =
  /\b(limited|ltd|plc|llc|llp|inc|incorporated|corp|corporation|co|company|lp|gp|gmbh|ag|kg|sa|sas|sarl|bv|nv|spa|srl|lda|ab|oy|as|kk|pte|pty|bhd|sdn)\.?$/i;

/** True when a stored company name looks like a raw source name the backend
 *  `display_name` would have cleaned up. Conservative on purpose — a false
 *  negative just leaves a name uncorrected; a false positive costs one cached
 *  fetch that finds no change. */
function looksUnstandardised(name: string): boolean {
  const t = name.trim();
  if (!t) return false;
  const letters = t.replace(/[^A-Za-z]/g, '');
  if (!letters) return false;
  // ALL-CAPS multi-letter registry form ("BLACKSTONE CAPITAL PARTNERS").
  // Short acronyms (≤3 letters, e.g. KKR / EQT / TPG) are legitimately
  // upper-case and must not be flagged.
  if (letters.length > 3 && t === t.toUpperCase()) return true;
  // Trailing legal suffix the algorithm would have dropped.
  if (LEGAL_SUFFIX_RE.test(t)) return true;
  return false;
}

/** True when a stored person name looks like a raw source name the backend
 *  `person_display_name_service` would have cleaned up. Conservative on
 *  purpose, same rationale as `looksUnstandardised` above. */
function looksUnstandardisedPerson(name: string): boolean {
  const t = name.trim();
  if (!t) return false;
  const letters = t.replace(/[^A-Za-z]/g, '');
  if (!letters) return false;
  // ALL-CAPS multi-word registry/scrape form ("JOHN SMITH").
  if (letters.length > 3 && t === t.toUpperCase()) return true;
  // "SMITH, JOHN" — reordered by the backend algorithm.
  if (t.includes(',')) return true;
  return false;
}

/** Display name derivation mirroring CompanyDetailPage: trust the backend
 *  `display_name`, fall back to the client heuristic only when it's absent. */
function resolveDisplayName(detail: {
  display_name?: string | null;
  name: string;
}): string {
  return detail.display_name?.trim() || normalizeCompanyName(detail.name);
}

/** Person analogue: trust the backend `canonical_name` (now computed via
 *  `person_display_name_service`); the client heuristic is only a defence
 *  against a blank/whitespace-only value. */
function resolvePersonDisplayName(detail: { canonical_name: string }): string {
  const trimmed = detail.canonical_name?.trim();
  return trimmed || normalizePersonName(trimmed ?? '');
}

export function useRehydrateFavourites(): void {
  const items = useFavouritesStore((s) => s.items);
  const patchFavourite = useFavouritesStore((s) => s.patchFavourite);

  const companySuspects: FavouriteItem[] = items.filter(
    (f) => f.kind === 'company' && looksUnstandardised(f.name),
  );
  const personSuspects: FavouriteItem[] = items.filter(
    (f) => f.kind === 'person' && looksUnstandardisedPerson(f.name),
  );

  const companyResults = useQueries({
    queries: companySuspects.map((f) => ({
      queryKey: ['favourite-rehydrate', 'company', f.id] as const,
      queryFn: () => fetchCompanyDetail(f.id),
      staleTime: Infinity,
      gcTime: 60 * 60 * 1000,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    })),
  });

  const personResults = useQueries({
    queries: personSuspects.map((f) => ({
      queryKey: ['favourite-rehydrate', 'person', f.id] as const,
      queryFn: () => fetchPersonaDetail(f.id),
      staleTime: Infinity,
      gcTime: 60 * 60 * 1000,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    })),
  });

  // Signature of resolved data so the effect only runs when a fetch lands.
  const signature = [
    ...companyResults.map((r, i) =>
      r.data ? `co:${companySuspects[i].id}:${r.data.display_name ?? ''}:${r.data.name}` : '',
    ),
    ...personResults.map((r, i) =>
      r.data ? `p:${personSuspects[i].id}:${r.data.canonical_name}` : '',
    ),
  ].join('|');

  useEffect(() => {
    companyResults.forEach((r, i) => {
      const detail = r.data;
      const fav = companySuspects[i];
      if (!detail || !fav) return;
      const patch: Partial<Pick<FavouriteItem, 'name' | 'logoUrl' | 'domain'>> = {
        name: resolveDisplayName(detail),
      };
      if (detail.logo_url != null) patch.logoUrl = detail.logo_url;
      if (detail.domain != null) patch.domain = detail.domain;
      patchFavourite('company', fav.id, patch);
    });
    personResults.forEach((r, i) => {
      const detail = r.data;
      const fav = personSuspects[i];
      if (!detail || !fav) return;
      patchFavourite('person', fav.id, { name: resolvePersonDisplayName(detail) });
    });
    // `signature` captures the resolved-data identity; patchFavourite is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
}
